import React, { useState, useEffect, useRef } from 'react';

import MerchantPermissionManager from '@components/shared/merchants/MerchantPermissionManager';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useCan from '@shared/permissions/useCan';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { operatorService } from '@services/operator/operatorService';
import { merchantService } from '@services/operator/merchantService';

const OperatorsMerchants = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const goBack = useSmartBack();
  const { operator: currentOperator } = useOperatorAuth();
  const { refreshOperatorData } = useOperatorData();
  const can = useCan();
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [error, setError] = useState(null);
  const managerRef = useRef();

  useEffect(() => {
    fetchOperator();
  }, [id]);

  const fetchOperator = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await operatorService.get(id);

      if (data.success && data.data) {
        setOperator(data.data);
      } else {
        throw new Error('Failed to fetch operator');
      }
    } catch (err) {
      console.error('Failed to fetch operator:', err);
      setError(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    await managerRef.current?.save();
    await fetchOperator();
    if (currentOperator && String(currentOperator.id) === String(id)) {
      refreshOperatorData();
    }
  };

  // 自編路徑已 redirect 到 Profile（見上方 useEffect），這裡只判權限
  const canEditMerchants = () => can('operators:write');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert message={t('common.error.title')} description={error} type="error" showIcon />
      </div>
    );
  }

  const operatorLabel = operator ? `${operator.name} (${operator.username})` : '';

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <PageHeader
        items={[
          'org_management',
          'org_management.operators',
          { label: operatorLabel, path: `/operators/${id}/edit` },
          { label: t('operator.operators.merchants.title', { defaultValue: '貨主權限' }) }
        ]}
      />

      <MerchantPermissionManager
        ref={managerRef}
        operatorService={operatorService}
        merchantService={merchantService}
        operatorMerchants={operator?.merchants || []}
        operatorId={id}
        canEdit={canEditMerchants()}
      />

      <FloatingActionBarSpacer />

      {canEditMerchants() && (
        <FloatingActionBar
          role="operator"
          onCancel={goBack}
          onSubmit={handleSubmit}
          loading={managerRef.current?.saving}
        />
      )}
    </div>
  );
};

export default OperatorsMerchants;
